import React, { useEffect, useRef, useState } from "react";
import { Flame, MessageCircle } from "lucide-react";
import { wa, logClick } from "../mock";
import { Lightbox } from "./Lightbox";
import { ProductIllustration } from "./ProductIllustration";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");

export default function FeaturedDeals() {
  const [items, setItems] = useState([]);
  const [lb, setLb] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/products?featured=true`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length === 0) return;
    const el = trackRef.current;
    if (!el) return;
    const id = setInterval(() => {
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const next = el.scrollLeft + 320;
      el.scrollTo({ left: next >= max - 4 ? 0 : next, behavior: "smooth" });
    }, 3200);
    return () => clearInterval(id);
  }, [items]);

  if (items.length === 0) return null;

  return (
    <section data-testid="featured-deals-section" className="home-section home-section--band" style={{ background: "var(--cream)" }}>
      <div className="container">
        <div className="reveal mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{ background: "linear-gradient(135deg, #ff3f1f, var(--orange-500))", color: "#fff" }}>
              <Flame size={22} />
            </span>
            <div>
              <h2 className="font-heading text-[24px] font-extrabold leading-tight" style={{ color: "var(--green-950)" }}>Hot Stock</h2>
              <p className="text-[13.5px]" style={{ color: "var(--muted)" }}>Fast-moving picks — grab before they’re gone</p>
            </div>
          </div>
          <a href="#stock" className="hidden sm:inline font-heading text-[14px] font-bold" style={{ color: "var(--orange-700)" }}>View all stock →</a>
        </div>

        <div ref={trackRef} className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth pb-2">
          {items.map((p) => (
            <article key={p.id} className="flex w-[280px] flex-shrink-0 flex-col overflow-hidden rounded-2xl border bg-white"
                     style={{ borderColor: "var(--line)", boxShadow: "0 12px 30px rgba(45,106,79,0.08)" }}>
              <div className="relative h-36 overflow-hidden" style={{ background: "var(--green-100)" }}>
                {p.img ? (
                  <img src={p.img} alt={p.name} loading="lazy"
                       onClick={() => setLb({ images: (p.images && p.images.length ? p.images : [p.img]).filter(Boolean), alt: p.name, url: `${window.location.origin}/product/${p.id}`, credit: (p.image_credit || p.image_kind === "reference") ? { text: p.image_credit || "", source: p.image_source_url || "", license: p.image_license || "", licenseUrl: p.image_license_url || "", changes: (p.image_changes && !/^\s*unmodified\b/i.test(p.image_changes)) ? p.image_changes : "", reference: p.image_kind === "reference" } : null })}
                       data-testid={`featured-photo-zoom-${p.id}`}
                       className={`h-full w-full cursor-zoom-in ${p.image_kind === "reference" ? "object-contain" : "object-cover"}`} />
                ) : (
                  <ProductIllustration p={p} />
                )}
                <span className="absolute left-3 top-3 z-10 rounded-full px-2.5 py-1 font-heading text-[11px] font-bold text-white"
                      style={{ background: "linear-gradient(135deg, #ff3f1f, var(--orange-500))" }}>★ Featured</span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--orange-700)" }}>{p.brand}</span>
                <h3 className="mt-1 font-sans text-[15px] font-medium leading-relaxed" style={{ color: "var(--ink)" }}>{p.name}</h3>
                <div className="mt-3 flex items-center justify-between">
                  {p.price_on_request ? (
                    <span className="font-heading text-[14px] font-extrabold" style={{ color: "var(--green-800)" }}>Price on request</span>
                  ) : (
                    <span className="font-heading text-[18px] font-extrabold" style={{ color: "var(--orange-700)" }}>{inr(p.price)}</span>
                  )}
                  <a href={wa(`Hi Kapatia, I'm interested in the featured item: ${p.name} (${p.brand}).`)}
                     onClick={() => logClick(p, "featured")}
                     target="_blank" rel="noopener noreferrer"
                     className="grid h-11 w-11 place-items-center rounded-full text-white"
                     style={{ background: "linear-gradient(135deg, var(--bright-green), var(--bright-green-strong))" }} aria-label="Enquire">
                    <MessageCircle size={16} />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      {lb && <Lightbox images={lb.images} alt={lb.alt} shareUrl={lb.url} onClose={() => setLb(null)} />}
    </section>
  );
}
